import {dispatch, EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import {projectUrl} from '../../../../directApi';
import RunNovaAgentProductionMutation from '../../../../mutations/nova/RunNovaAgentProductionMutation';
import type {RunNovaAgentProductionMutation$data} from '../../../../mutations/nova/__generated__/RunNovaAgentProductionMutation.graphql';
import Util from '../../util/util';
import type {NovaAgentSummary} from './novaAgentSummary';
import {addNotification, isTaskCurrentlyViewed} from './novaNotificationStore';

// Empty, not MOCK_AGENTS: the picker already renders a stated empty state for an empty list
// (nova_invocation.picker.empty_state), so there is no need for a placeholder catalogue to cover
// the gap before the real fetch resolves. Seeding this with the fixture would mean a customer with
// `nova_agent_invocation` on sees nine invented agents ("General", "FAQ writer", ...) until the
// fetch lands — and indefinitely if it fails, or if useNovaAvailableAgents' fetch is ever lost to
// an unmount before it resolves. Tests that want the fixture's agents ask for them explicitly via
// setAvailableAgents(MOCK_AGENTS), the same as any other test fixture.
let availableAgents: NovaAgentSummary[] = [];

export function getAvailableAgents(): NovaAgentSummary[] {
	return availableAgents;
}

// Whether a bare Action click has anything to run. False in three distinct situations that are
// indistinguishable from here and want the same answer from the control: the fetch has not resolved
// yet, it resolved to an account with no granted agents, and it degraded to `[]` because the run
// service was unreachable (ViewerType.js resolves novaAgentsForInvocation with `.catch(() => [])`,
// so a blip costs the list rather than the whole viewer query).
//
// Deliberately not split into pending/empty. A separate "still loading" state would need a disabled
// reason of its own — new copy, a new member of the reason vocabulary — to say something the person
// cannot act on either way, and every one of the three means the same thing to them right now: this
// control has no agent to run. The first-load window it covers is one per session, not one per row.
export function hasAgentToRun(): boolean {
	return availableAgents.length > 0;
}

// The one write path once real data exists: a fetch resolving to the customer's actual agent
// list calls this once, and every picker instance — there is one store, read by all of them —
// sees the same list from then on. Dispatches so an already-open picker (or one that opened
// before a slow fetch resolved) updates live rather than only on its next mount.
export function setAvailableAgents(agents: NovaAgentSummary[]): void {
	availableAgents = agents;
	dispatch(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, undefined);
}

export function subscribeToAvailableAgents(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<void>(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, handler);
	return () => unsubscribe<void>(EVENT_ID.NOVA_AVAILABLE_AGENTS_CHANGED, handler);
}

// The notification names the agent that ran. An id that no longer resolves — the list was
// re-fetched mid-run, or the grant was revoked while it was in flight — falls back to the product
// name, never to another agent's title: attributing a run to an agent that did not produce it is
// worse than naming no agent at all. The previous fallback reached for whichever agent claimed to
// be the account default and then for the literal 'General', which is a fixture agent's name, so an
// unresolvable id could put an agent that does not exist in front of a customer.
function resolveAgentName(agentId?: string): string {
	return availableAgents.find(a => a.id === agentId)?.title ?? 'Nova';
}

// 'workflow' matches the same project-landing-page slug the real notification system's own link
// builder falls back to (Notification.js); the /T<companyTaskId> suffix matches its established
// convention for auto-opening a specific task's modal on arrival. No customId is threaded through
// from callers, so this always uses projectUrl's own P-<id> fallback form — a real, working URL,
// just not the prettier custom-slug one.
function buildTaskUrl(companyProjectId?: string | number, companyTaskId?: string | number): string | undefined {
	if (!companyProjectId || !companyTaskId) return undefined;
	return `${projectUrl(companyProjectId, undefined)}/workflow/T${companyTaskId}`;
}

// The run service's own failure types, as the saved record's `failureState` reports them. Only the
// types whose customer-facing state this side has copy for are mapped here; completing the table
// against the full taxonomy, and the copy for each, belongs with the failure-presentation work.
// `generic` is what an unmapped type resolves to — a stated generic is honest, where reporting it
// as some other named cause would not be.
//
// The bar for an entry here is copy that already ships: a type left out of this table whose cause
// has a translated string and a CAUSE_MESSAGE_ID entry is not an incomplete table, it is shipped
// copy that nothing can reach. `document_processing` and `throttled` were both in that position.
export type FailureCause =
	| 'missing_access'
	| 'timeout'
	| 'unavailable_model'
	| 'permission'
	| 'document_processing'
	| 'at_capacity'
	| 'generic';

const FAILURE_CAUSE_BY_RECORD_STATE: Record<string, FailureCause> = {
	missing_access: 'missing_access',
	// A definition the run service could not assemble — an absent agent record, or a record naming a
	// version it does not hold. It maps this onto the same customer-facing state as a source it could
	// not read, because from the caller's side both are "something this run needed was not there".
	missing_input: 'missing_access',
	timeout: 'timeout',
	model_unavailable: 'unavailable_model',
	model_retired: 'unavailable_model',
	// A source the run service could read but not parse. Its own cause, rather than folded into
	// missing_access: the remedy is the document, not the grant.
	document_processing: 'document_processing',
	// The model was busy and did not answer within the run deadline. Capacity, not breakage — and
	// at_capacity's copy, which promises that trying again shortly works, is exactly what this is.
	throttled: 'at_capacity',
};

// Refusals the platform raises before a run starts. They reach this control through the mutation's
// error path rather than on a saved record, so they need no cause of their own here — but they are
// known names, and warning about them as unmapped gaps would be wrong.
const KNOWN_REFUSAL_STATES = new Set(['service_paused', 'run_ceiling', 'budget_exhausted']);

// An unrecognised failure type is a mapping gap rather than a bug in the run: the run really did
// fail, and saying so generically is the truthful report. Warned rather than swallowed so the gap
// is visible in the console of whoever hits it first.
export function failureCauseFromRecord(failureState?: string | null): FailureCause {
	if (!failureState) return 'generic';
	if (KNOWN_REFUSAL_STATES.has(failureState)) return 'generic';
	const mapped = FAILURE_CAUSE_BY_RECORD_STATE[failureState];
	if (!mapped) {
		// eslint-disable-next-line no-console
		console.warn(`nova run failed with an unmapped failure type: ${failureState}`);
		return 'generic';
	}
	return mapped;
}

// The refusal codes NovaAgentLogic sets on the GraphQLError it throws before a run starts
// (REFUSAL_FAILURE_TYPE_BY_CODE). Only RUN_CEILING is a "not now": BUDGET_EXHAUSTED and
// SERVICE_PAUSED are refusals that trying again shortly does not clear, so mapping them onto
// at_capacity's copy would promise a retry that cannot work. They stay generic until they have
// copy of their own.
const FAILURE_CAUSE_BY_REFUSAL_CODE: Record<string, FailureCause> = {
	RUN_CEILING: 'at_capacity',
};

// The GraphQL errors a Relay mutation failure carries, if it carries any. `createRequestError`
// (react-relay-network-modern) formats only each error's `message` and locations into the Error's
// own message — `extensions` never reaches the string — but it also hangs the whole response off
// the error as `res`, and `res.errors` is the untouched errors array, extensions and all. That is
// the only place a refusal's code survives on this side.
//
// Read defensively rather than cast: this walks an error shape the type system knows nothing about,
// and a batching or middleware change upstream could stop producing it without a compile error.
function refusalCodesOf(error: unknown): string[] {
	const res = (error as {res?: unknown} | null)?.res;
	const errors = (res as {errors?: unknown} | null)?.errors;
	if (!Array.isArray(errors)) return [];
	return errors
		.map(entry => (entry as {extensions?: {code?: unknown}} | null)?.extensions?.code)
		.filter((code): code is string => typeof code === 'string');
}

// A refusal the platform raised before the run started arrives as an error rather than a saved
// record, so it carries no failure type to map — only the code it was thrown with. A capacity
// refusal is the one that must not read as "something went wrong": it is a "not now", and the
// platform's own copy already says trying again shortly will work.
//
// Keyed on the code rather than on the message this previously matched: the code is a contract
// between this surface and NovaAgentLogic, where the message is customer-facing copy that the
// translations workflow can change underneath a regex without anything failing.
export function failureCauseFromRefusal(error: unknown): FailureCause {
	for (const code of refusalCodesOf(error)) {
		const mapped = FAILURE_CAUSE_BY_REFUSAL_CODE[code];
		if (mapped) return mapped;
	}
	return 'generic';
}

export type RunOutcome = {kind: 'succeeded'} | {kind: 'failed'; cause: FailureCause} | {kind: 'cancelled'};

export interface RunState {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	outcome: RunOutcome | null;
}

// Once three tasks are running at once, every other idle control disables itself with a reason
// until one finishes. This is a client-simulated ceiling over this same store, not a real
// account-wide limit (that depends on a platform-side capacity check that doesn't exist yet) —
// it mirrors the mockup's own local running-count check.
export const RUN_CAP = 3;

const runStates = new Map<string, RunState>();

// Tracks how many tasks are currently running, kept in step with the two places below that flip
// isRunning, rather than scanning the whole map on every read — the map only grows across a
// session and this count is read on every mounted control's mount and on every state change.
let runningCount = 0;

// A run is identified by its generation, not by its task: a task can have a stopped run still in
// flight while a new one starts, and "is this task running" cannot tell those two apart. Bumped by
// both invoke() and stop(), so a superseded run's late result recognises that it is no longer the
// run this task is waiting on and does nothing.
const runGeneration = new Map<string, number>();

function nextGeneration(taskId: string): number {
	const next = (runGeneration.get(taskId) ?? 0) + 1;
	runGeneration.set(taskId, next);
	return next;
}

function readState(taskId: string): RunState {
	return runStates.get(taskId) ?? {hasEverRun: false, isRunning: false, outputRead: false, outcome: null};
}

function writeState(taskId: string, next: RunState): void {
	runStates.set(taskId, next);
	dispatch(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, taskId);
}

export function getRunState(taskId: string): RunState {
	return readState(taskId);
}

export function getRunningCount(): number {
	return runningCount;
}

export function isAtRunCap(): boolean {
	return runningCount >= RUN_CAP;
}

// Stands in for the platform's real account-wide budget/capacity check, which doesn't exist yet.
// Derives the same near/at-limit shape from this local run-cap simulation — swap the body, not
// the callers, once a real account-level check exists.
export function getAccountLimitNotice(): 'near_limit' | 'at_limit' | null {
	if (runningCount >= RUN_CAP) return 'at_limit';
	if (runningCount === RUN_CAP - 1) return 'near_limit';
	return null;
}

// What `invoke()` needs back from a run, whoever performed it. Tests supply their own runner
// instead of a real commit, which is why this is the seam rather than the mutation module itself.
export type RunAgent = (args: {taskId: string; agentId: string}) => Promise<RunOutcome>;

// `graphql-relay` is a server-side dependency and is not available here, so the global id is
// encoded the same way the rest of web-front does it — plain base64 of "Type:rawId", which is what
// the platform's own `fromGlobalId` decodes. Not the ticketing tree's base64url variant: that
// strips padding for a different gateway's wire shape.
const novaAgentGlobalId = (agentId: string): string => btoa(`NovaAgent:${agentId}`);

// `taskId` arrives already a Relay global id — every mounting surface passes `task.id` from its own
// Relay data. The agent id does not: the picker's list comes from `viewer.novaAgentsForInvocation`,
// whose `agentId` is the agent's own string id, so it is encoded here to the global id the mutation
// decodes. Getting this backwards fails three services away, at the run service's own id check.
const commitRun: RunAgent = ({taskId, agentId}) =>
	new Promise(resolve => {
		Util.CommitMutation(
			RunNovaAgentProductionMutation,
			{id: novaAgentGlobalId(agentId), taskId},
			(response: RunNovaAgentProductionMutation$data) => {
				const run = response?.runNovaAgentProduction?.result?.run;
				if (run?.outcome === 'SUCCEEDED') {
					resolve({kind: 'succeeded'});
					return;
				}
				if (run?.outcome === 'FAILED') {
					resolve({kind: 'failed', cause: failureCauseFromRecord(run.failureState)});
					return;
				}
				// Neither terminal outcome came back. The run is not reported as succeeded on a shape
				// nothing confirmed, but nor is a named cause invented for it — generic is what the
				// record actually supports, and the warning is what makes the unread shape findable.
				// eslint-disable-next-line no-console
				console.warn(`nova run returned no terminal outcome (status ${run?.status ?? 'unknown'})`);
				resolve({kind: 'failed', cause: 'generic'});
			},
			true,
			null,
			// A refusal the platform raised before the run started — at the run ceiling, over budget,
			// the feature not enabled — arrives here rather than as a saved FAILED record. It is still
			// a run that produced nothing, so it resolves as a failure rather than leaving the control
			// stuck on Working. Passing an onError at all is what stops Util.CommitMutation opening its
			// generic error modal over a state this control already states on itself.
			//
			// That it arrives here at all is worth stating, because the intuitive guess is the opposite
			// and it would make this branch dead code: a refusal is thrown *after* the resolver has
			// produced a `data` payload, so one might expect Relay to treat it as a partial success and
			// call onCompleted. It does not. `fetchWithMiddleware` (react-relay-network-modern) throws
			// `createRequestError` whenever `res.errors` is present, whether or not `res.data` is —
			// so a RUN_CEILING refusal genuinely reaches this handler and never onCompleted.
			//
			// The refusal is also logged rather than only mapped: the cause below narrows it to what the
			// surface can say, where the platform's own error carries its code and correlation id, which
			// is the only thing that identifies which refusal it actually was.
			(error: unknown) => {
				// eslint-disable-next-line no-console
				console.warn('nova run was refused before it started:', error);
				resolve({kind: 'failed', cause: failureCauseFromRefusal(error)});
			}
		);
	});

export function invoke(
	taskId: string,
	agentId?: string,
	opts?: {
		runAgent?: RunAgent;
		taskName?: string;
		companyProjectId?: string | number;
		companyTaskId?: string | number;
		projectColor?: string;
	}
): Promise<void> {
	const current = readState(taskId);
	// A second call for a task already mid-run would otherwise run it twice and let the later
	// result overwrite the earlier one, which could silently revert a read output back to unread.
	if (current.isRunning) return Promise.resolve();
	// The cap is also enforced presentationally in deriveControlState, but that is the UI's
	// rendering of the invariant rather than the invariant itself — NovaAgentPickerForTask calls
	// invoke() directly, bypassing the disabled-button gate entirely.
	if (isAtRunCap()) return Promise.resolve();
	// A run needs an agent. deriveControlState disables the control whenever the resolved list is
	// empty (see its no_agent_available branch), so reaching this from the control itself is not an
	// expected state — but NovaAgentPickerForTask calls invoke() directly, and nothing stops a
	// future caller doing the same, so the guard stays. Warned rather than returned silently: a
	// click that produces no state change, no notification and no log is indistinguishable from a
	// broken control, which is exactly how this read before.
	if (!agentId) {
		// eslint-disable-next-line no-console
		console.warn(`nova run was requested for task ${taskId} with no agent to run`);
		return Promise.resolve();
	}
	runningCount += 1;
	const generation = nextGeneration(taskId);
	writeState(taskId, {...current, isRunning: true, outcome: null});

	const runAgent = opts?.runAgent ?? commitRun;
	return runAgent({taskId, agentId})
		.catch((): RunOutcome => {
			// A runner that threw rather than reporting an outcome. The run still produced nothing, and
			// the state machine must leave Working either way — stranding the control there is worse
			// than reporting a generic failure.
			// eslint-disable-next-line no-console
			console.warn('nova run threw instead of reporting an outcome');
			return {kind: 'failed', cause: 'generic'};
		})
		.then(outcome => {
			// Superseded while this was in flight — stopped, or stopped and re-invoked. Either way this
			// is no longer the run the task is waiting on: applying its result would double-decrement
			// the cap count and write an answer nothing asked for over whatever replaced it.
			if (runGeneration.get(taskId) !== generation) return;
			runningCount -= 1;
			writeState(taskId, {
				// Only a real answer counts as "ever run" for Ready/Continue purposes — a failed run has
				// nothing to review, so it must not read as Ready.
				hasEverRun: outcome.kind === 'succeeded',
				isRunning: false,
				outputRead: false,
				outcome,
			});
			if ((outcome.kind === 'succeeded' || outcome.kind === 'failed') && !isTaskCurrentlyViewed(taskId)) {
				addNotification({
					taskId,
					taskName: opts?.taskName ?? taskId,
					agentName: resolveAgentName(agentId),
					kind: outcome.kind,
					taskUrl: buildTaskUrl(opts?.companyProjectId, opts?.companyTaskId),
					companyTaskId: opts?.companyTaskId,
					projectColor: opts?.projectColor,
					companyProjectId: opts?.companyProjectId,
				});
			}
		});
}

// A local cancel only: it returns this control to its pre-run state and frees the run-cap slot, but
// it cannot stop the run already in flight — whether the run service can cancel a run at all is an
// open question, and the real stop belongs with the failure-and-stop work. Bumping the generation is
// what supersedes the in-flight run, so its own resolution is ignored however long it takes and
// whatever the task is doing by then — including a newer run started in the meantime.
export function stop(taskId: string): void {
	const current = readState(taskId);
	if (!current.isRunning) return;
	nextGeneration(taskId);
	runningCount -= 1;
	writeState(taskId, {hasEverRun: current.hasEverRun, isRunning: false, outputRead: false, outcome: {kind: 'cancelled'}});
}

export function markRead(taskId: string): void {
	const current = readState(taskId);
	if (!current.hasEverRun || current.isRunning) return;
	writeState(taskId, {...current, outputRead: true});
}

export function subscribeToRunState(taskId: string, callback: () => void): () => void {
	const handler = (changedTaskId: string): void => {
		if (changedTaskId === taskId) callback();
	};
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

// The run cap and the row-highlight-on-unread-Ready feature are both properties of the whole
// tracked set, not of one taskId — a plain per-task subscription can't tell an idle task that a
// caller renders elsewhere just now hit (or dropped below) the ceiling.
export function subscribeToAnyRunStateChange(callback: () => void): () => void {
	const handler = (): void => callback();
	subscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
	return () => unsubscribe<string>(EVENT_ID.NOVA_INVOCATION_STATE_CHANGED, handler);
}

export function isUnreadReady(taskId: string): boolean {
	const state = readState(taskId);
	// hasEverRun can be stale-true from an earlier success carried through a later re-invoke (see
	// invoke()'s spread of `current`) — a failed/cancelled outcome on that later attempt means
	// there is no new output to flag as ready, regardless of what an older run left behind.
	const outcomeBlocksReady = state.outcome?.kind === 'failed' || state.outcome?.kind === 'cancelled';
	return state.hasEverRun && !state.isRunning && !state.outputRead && !outcomeBlocksReady;
}
